import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';

// ── Design tokens ──────────────────────────────────────────────────────────
const NAVY    = '#1E3A4A';
const ACCENT  = '#F5C518';
const BORDER  = '#E5E7EB';
const MUTED   = '#64748B';
const TEXT    = '#0F172A';
const GREEN   = '#16A34A';
const RED     = '#DC2626';
const AMBER   = '#92400E';
const SUBTLE  = '#FAFAF7';

// Australian FY ending year of "today"
function currentFyEnd() {
  const n = new Date();
  return n.getMonth() + 1 >= 7 ? n.getFullYear() + 1 : n.getFullYear();
}
function fmtMoney(n)  { return '$' + (Number(n) || 0).toFixed(2); }
function fmtMoney0(n) { return '$' + Math.round(Number(n) || 0).toLocaleString('en-AU'); }
function fmtDateDMY(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// ── Main component ─────────────────────────────────────────────────────────
export default function AccountsPanel() {
  const [fy, setFy] = useState(currentFyEnd());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await api.getAccountantPack(fy);
      setData(d);
    } catch (e) { setError(e.message || 'Failed to load.'); }
    finally    { setLoading(false); }
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center', color: MUTED }}>Loading accountant pack…</div>;
  if (error)            return <div style={{ padding: 24, color: RED }}>{error}</div>;
  if (!data)            return null;

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 16,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: TEXT }}>
            Accountant Pack — {data.fy.label}
          </h2>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>
            Period: {fmtDateDMY(data.fy.start)} → {fmtDateDMY(data.fy.end_date)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>FY:</span>
          <select value={fy} onChange={e => setFy(parseInt(e.target.value))} style={selectStyle}>
            {[currentFyEnd() + 1, currentFyEnd(), currentFyEnd() - 1, currentFyEnd() - 2].map(y =>
              <option key={y} value={y}>FY {y - 1}–{String(y).slice(2)}</option>
            )}
          </select>
        </div>
      </div>

      {/* 1. EOFY checklist */}
      <Section title="1. EOFY Readiness Checklist" subtitle={`${data.checklist.filter(c => c.done).length} of ${data.checklist.length} complete`}>
        <Checklist items={data.checklist} />
      </Section>

      {/* 2. Profit & Loss */}
      <Section
        title="2. Profit & Loss Statement"
        subtitle={data.pnl_zoho ? 'Official figures from Zoho Books · app detail below' : 'Income and expenses for the FY (app estimate — Zoho unavailable)'}
      >
        {data.pnl_zoho && <ZohoPnlCard z={data.pnl_zoho} />}
        <ProfitLoss pnl={data.pnl} />
      </Section>

      {/* 3. Contractor Payments (TPAR) */}
      <Section title="3. Contractor Payments (TPAR)" subtitle={`⚠ TPAR mandatory for cleaning services · Due ${data.tpar_due}`}>
        <ContractorTpar contractors={data.contractors} />
      </Section>

      {/* 4. BAS Monthly */}
      <Section title="4. BAS Monthly Summary" subtitle="Reporting frequency: Monthly · Due 21st of next month">
        <BasMonthly rows={data.bas_monthly.rows} />
      </Section>

      {/* 5. Asset Register */}
      <Section title="5. Asset Register & Depreciation" subtitle={`Total deductible this FY: ${fmtMoney(data.assets.total_deductible)}`}>
        <AssetRegister rows={data.assets.rows} totalDeductible={data.assets.total_deductible} />
      </Section>

      {/* 6. Expenses by Category */}
      <Section title="6. Expenses by Category" subtitle={`Total: ${fmtMoney(data.expenses_total.total_inc_gst)} (inc GST)`}>
        <ExpensesByCategory rows={data.expenses_by_category} totals={data.expenses_total} />
      </Section>

      {/* 7. Bank Reconciliation */}
      <Section title="7. Bank Reconciliation Status" subtitle="Manual snapshot per account">
        <BankRecon rows={data.bank_reconciliation} onChange={load} />
      </Section>

      {/* Generate ZIP button */}
      <GeneratePackButton fy={fy} fyLabel={data.fy.label} />
    </div>
  );
}

function GeneratePackButton({ fy, fyLabel }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleGenerate() {
    setBusy(true); setError('');
    try {
      const blob = await api.generateAccountantPackZip(fy);
      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Acts_Accountant_Pack_${fyLabel.replace(/[^A-Za-z0-9]+/g, '_')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setError(e.message || 'Failed to generate pack.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      background: 'linear-gradient(155deg, #1E3A4A 0%, #0F2233 100%)',
      borderRadius: 16, padding: '20px 24px', marginBottom: 24,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 12,
      boxShadow: '0 4px 16px rgba(15,35,51,0.2)',
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#F5C518', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
          Accountant Pack
        </div>
        <div style={{ fontSize: 13, color: '#CBD5E1' }}>
          Download a single ZIP with PDFs + Excel for everything above — ready to email the accountant.
        </div>
      </div>
      <button
        onClick={handleGenerate}
        disabled={busy}
        style={{
          padding: '14px 26px', borderRadius: 10, border: 'none',
          background: busy ? '#94A3B8' : '#F5C518',
          color: busy ? '#fff' : '#0F172A',
          fontWeight: 900, fontSize: 14, cursor: busy ? 'wait' : 'pointer',
          letterSpacing: 0.5, whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(245,197,24,0.4)',
        }}
      >
        {busy ? '⚙ Building pack…' : '📦 Generate ZIP Pack'}
      </button>
      {error && (
        <div style={{ flex: '1 1 100%', background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14,
      padding: '18px 22px', marginBottom: 14, boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    }}>
      <div style={{ marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${BORDER}` }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: TEXT, letterSpacing: 0.2 }}>{title}</h3>
        {subtitle && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

// ── 1. Checklist ───────────────────────────────────────────────────────────
function Checklist({ items }) {
  const done = items.filter(i => i.done).length;
  const pct  = Math.round((done / items.length) * 100);
  return (
    <div>
      {/* Progress bar */}
      <div style={{ background: '#F1F5F9', borderRadius: 999, height: 8, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: GREEN, transition: 'width 0.3s' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8 }}>
        {items.map(i => (
          <div key={i.key} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px', borderRadius: 8,
            background: i.done ? '#F0FDF4' : '#FEF2F2',
            border: `1px solid ${i.done ? '#BBF7D0' : '#FECACA'}`,
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
              background: i.done ? GREEN : RED, color: '#fff',
              fontSize: 12, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {i.done ? '✓' : '✗'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{i.label}</div>
              {i.note && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{i.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 2a. Official Zoho Books P&L card — leads the section; the app detail
//        below is the operational estimate. ─────────────────────────────────
function ZohoPnlCard({ z }) {
  const stale = z.source === 'zoho-stale';
  return (
    <div style={{
      border: '2px solid #16a34a', borderRadius: 12, padding: '14px 16px',
      marginBottom: 14, background: '#F0FDF4',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#15803D', letterSpacing: 1 }}>
          📊 OFFICIAL — ZOHO BOOKS
        </span>
        {stale && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '2px 8px', borderRadius: 999 }}>
            last snapshot — live fetch failed
          </span>
        )}
        <span style={{ fontSize: 10.5, color: MUTED, marginLeft: 'auto' }}>
          These are the accountant's numbers · app detail below is the operational estimate
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div>
          <div style={{ fontSize: 10.5, color: MUTED, fontWeight: 700, textTransform: 'uppercase' }}>Total Income</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: NAVY }}>{fmtMoney0(z.revenue)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: MUTED, fontWeight: 700, textTransform: 'uppercase' }}>Total Expenses</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#DC2626' }}>{fmtMoney0(z.total_expense)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: MUTED, fontWeight: 700, textTransform: 'uppercase' }}>Net Profit Before Tax</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: z.net_profit_before_tax >= 0 ? '#15803D' : '#DC2626' }}>
            {fmtMoney0(z.net_profit_before_tax)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: MUTED, fontWeight: 700, textTransform: 'uppercase' }}>
            Est. Company Tax ({Math.round(z.company_tax_rate * 100)}%)
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#92400E' }}>{fmtMoney0(z.estimated_company_tax)}</div>
        </div>
      </div>
    </div>
  );
}

// ── 2. Profit & Loss ───────────────────────────────────────────────────────
function ProfitLoss({ pnl }) {
  return (
    <div>
      {/* Income */}
      <PnlBlock title="Income" tone={GREEN}>
        <PnlLine label="Service Revenue (KIA + Suzuki + GWM + GAC + Service)" value={pnl.income.service_revenue} />
        <PnlLine label="Used Cars Detailing" value={pnl.income.used_cars} />
        <PnlLine label="Office Cleaning"     value={pnl.income.office_cleaning} />
        <PnlLine label="Other Income"        value={pnl.income.other_income} />
        <PnlLine label="Gross Income (ex GST)" value={pnl.income.total_ex_gst} bold />
      </PnlBlock>

      {/* Expenses */}
      <PnlBlock title="Expenses" tone={RED}>
        <PnlLine label="Contractor Payments" value={pnl.expenses.contractor_payments} />
        {pnl.expenses.by_category.map(c => (
          <PnlLine key={c.category} label={c.category} value={c.amount_ex_gst} sub={`${c.count} item${c.count === 1 ? '' : 's'}`} />
        ))}
        {pnl.expenses.by_category.length === 0 && (
          <div style={{ fontSize: 12, color: MUTED, fontStyle: 'italic', padding: '6px 0' }}>No itemised expenses logged this FY.</div>
        )}
        <PnlLine label="Total Expenses" value={pnl.expenses.total} bold />
      </PnlBlock>

      {/* Bottom highlight */}
      <div style={{
        background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
        border: '1px solid #FCD34D', borderRadius: 12, padding: 14, marginTop: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: AMBER, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Net Profit Before Tax
          </span>
          <span style={{ fontSize: 22, fontWeight: 900, color: pnl.net_profit_before_tax >= 0 ? '#15803D' : '#991B1B' }}>
            {fmtMoney(pnl.net_profit_before_tax)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: AMBER }}>
            Estimated Company Tax ({Math.round(pnl.company_tax_rate * 100)}%)
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: AMBER }}>
            {fmtMoney(pnl.estimated_company_tax)}
          </span>
        </div>
      </div>
    </div>
  );
}
function PnlBlock({ title, tone, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: tone, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ background: SUBTLE, borderRadius: 8, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}
function PnlLine({ label, value, sub, bold }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: '8px 14px', borderBottom: `1px solid ${BORDER}`,
      background: bold ? '#fff' : 'transparent',
      fontWeight: bold ? 800 : 500,
    }}>
      <span style={{ fontSize: 13, color: bold ? TEXT : '#374151' }}>
        {label}
        {sub && <span style={{ fontSize: 11, color: MUTED, marginLeft: 6 }}>· {sub}</span>}
      </span>
      <span style={{ fontSize: bold ? 15 : 13, color: TEXT, fontVariantNumeric: 'tabular-nums' }}>
        {fmtMoney(value)}
      </span>
    </div>
  );
}

// ── 3. Contractor TPAR ─────────────────────────────────────────────────────
function ContractorTpar({ contractors }) {
  const { rows, missing_abn_count } = contractors;
  const total = rows.reduce((s, r) => s + r.total_paid, 0);
  return (
    <div>
      {missing_abn_count > 0 && (
        <div style={{
          background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8,
          padding: 10, marginBottom: 12, fontSize: 13, color: '#991B1B',
        }}>
          <strong>{missing_abn_count} contractor{missing_abn_count === 1 ? '' : 's'} missing an ABN.</strong>
          {' '}Without an ABN you may need to withhold 47% PAYG. Update profiles in Settings → Contractors.
        </div>
      )}
      <div style={{ overflow: 'auto', borderRadius: 8, border: `1px solid ${BORDER}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <Th>Contractor</Th>
              <Th>ABN</Th>
              <Th>Type</Th>
              <Th align="right">Hours / Jobs</Th>
              <Th align="right">Total Paid YTD</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><Td colSpan={5} align="center" style={{ color: MUTED, padding: 20 }}>No contractor payments recorded for this FY.</Td></tr>
            )}
            {rows.map(c => {
              const abnMissing = !c.abn || !String(c.abn).trim();
              return (
                <tr key={c.id} style={{ background: abnMissing ? '#FEF2F2' : 'transparent', borderBottom: '1px solid #F1F5F9' }}>
                  <Td><span style={{ fontWeight: 700 }}>{c.name}</span></Td>
                  <Td>{abnMissing ? <span style={{ color: RED, fontWeight: 700 }}>MISSING</span> : c.abn}</Td>
                  <Td><span style={{ fontSize: 11, color: MUTED, textTransform: 'capitalize' }}>{c.type.replace('_', ' ')}</span></Td>
                  <Td align="right" style={{ color: MUTED, fontSize: 12 }}>
                    {c.type === 'office_cleaner' ? `${c.days_count} days` : `${c.jobs_count} jobs · ${c.days_count} days`}
                  </Td>
                  <Td align="right" style={{ fontWeight: 800, color: GREEN }}>{fmtMoney(c.total_paid)}</Td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ background: '#FAFAF7', borderTop: `2px solid ${BORDER}` }}>
                <Td colSpan={4} style={{ fontWeight: 800, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, color: MUTED }}>
                  Total Contractors
                </Td>
                <Td align="right" style={{ fontWeight: 900, fontSize: 14, color: TEXT }}>{fmtMoney(total)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── 4. BAS Monthly ─────────────────────────────────────────────────────────
function BasMonthly({ rows }) {
  // Group months into FY quarters for collapsibility
  // Q1: Jul-Sep (months 7,8,9), Q2: Oct-Dec (10,11,12), Q3: Jan-Mar (1,2,3), Q4: Apr-Jun (4,5,6)
  const quarters = [
    { num: 1, label: 'Q1 · Jul–Sep', months: [7, 8, 9] },
    { num: 2, label: 'Q2 · Oct–Dec', months: [10, 11, 12] },
    { num: 3, label: 'Q3 · Jan–Mar', months: [1, 2, 3] },
    { num: 4, label: 'Q4 · Apr–Jun', months: [4, 5, 6] },
  ];
  const [open, setOpen] = useState({ 1: false, 2: false, 3: false, 4: false });
  const total = rows.reduce((s, r) => ({
    g1: s.g1 + r.g1, a1: s.a1 + r.a1, b1: s.b1 + r.b1, net: s.net + r.net,
  }), { g1: 0, a1: 0, b1: 0, net: 0 });

  return (
    <div style={{ overflow: 'auto', borderRadius: 8, border: `1px solid ${BORDER}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <Th>Period</Th>
            <Th align="right">G1 Total Sales</Th>
            <Th align="right">1A GST Sales</Th>
            <Th align="right">1B GST Purchases</Th>
            <Th align="right">Net GST</Th>
            <Th align="center">Status</Th>
          </tr>
        </thead>
        <tbody>
          {quarters.map(q => {
            const qRows = rows.filter(r => q.months.includes(r.month));
            const qTotal = qRows.reduce((s, r) => ({
              g1: s.g1 + r.g1, a1: s.a1 + r.a1, b1: s.b1 + r.b1, net: s.net + r.net,
            }), { g1: 0, a1: 0, b1: 0, net: 0 });
            return (
              <React.Fragment key={q.num}>
                <tr onClick={() => setOpen(o => ({ ...o, [q.num]: !o[q.num] }))}
                    style={{ background: '#EEF3F7', cursor: 'pointer', userSelect: 'none', borderBottom: `1px solid ${BORDER}` }}>
                  <Td style={{ fontWeight: 800, color: NAVY }}>
                    <span style={{ marginRight: 6, fontSize: 10, color: MUTED }}>{open[q.num] ? '▼' : '▶'}</span>
                    {q.label}
                  </Td>
                  <Td align="right" style={{ fontWeight: 700 }}>{fmtMoney(qTotal.g1)}</Td>
                  <Td align="right" style={{ fontWeight: 700 }}>{fmtMoney(qTotal.a1)}</Td>
                  <Td align="right" style={{ fontWeight: 700 }}>{fmtMoney(qTotal.b1)}</Td>
                  <Td align="right" style={{ fontWeight: 700, color: qTotal.net >= 0 ? '#991B1B' : GREEN }}>
                    {fmtMoney(qTotal.net)}
                  </Td>
                  <Td align="center" />
                </tr>
                {open[q.num] && qRows.map(r => (
                  <tr key={`${r.year}-${r.month}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <Td style={{ paddingLeft: 36, color: MUTED, fontSize: 12 }}>{r.label}</Td>
                    <Td align="right">{fmtMoney(r.g1)}</Td>
                    <Td align="right">{fmtMoney(r.a1)}</Td>
                    <Td align="right">{fmtMoney(r.b1)}</Td>
                    <Td align="right" style={{ fontWeight: 700, color: r.net >= 0 ? '#991B1B' : GREEN }}>
                      {fmtMoney(r.net)}
                    </Td>
                    <Td align="center">
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                        background: r.status === 'Lodged' ? '#D1FAE5' : '#FEF3C7',
                        color:      r.status === 'Lodged' ? '#065F46' : AMBER,
                      }}>{r.status}</span>
                    </Td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: '#FAFAF7', borderTop: `2px solid ${BORDER}` }}>
            <Td style={{ fontWeight: 800, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, color: MUTED }}>
              FY Total
            </Td>
            <Td align="right" style={{ fontWeight: 900 }}>{fmtMoney(total.g1)}</Td>
            <Td align="right" style={{ fontWeight: 900 }}>{fmtMoney(total.a1)}</Td>
            <Td align="right" style={{ fontWeight: 900 }}>{fmtMoney(total.b1)}</Td>
            <Td align="right" style={{ fontWeight: 900, color: total.net >= 0 ? '#991B1B' : GREEN }}>{fmtMoney(total.net)}</Td>
            <Td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── 5. Asset Register ──────────────────────────────────────────────────────
function AssetRegister({ rows, totalDeductible }) {
  const METHOD_LABELS = {
    instant_writeoff:   'Instant Write-off',
    prime_cost:         'Prime Cost',
    diminishing_value:  'Diminishing Value',
  };
  return (
    <div style={{ overflow: 'auto', borderRadius: 8, border: `1px solid ${BORDER}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <Th>Asset</Th>
            <Th>Purchase Date</Th>
            <Th align="right">Cost ex GST</Th>
            <Th>Method</Th>
            <Th align="right">Deduction This FY</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><Td colSpan={5} align="center" style={{ color: MUTED, padding: 20 }}>No assets registered.</Td></tr>
          )}
          {rows.map(a => (
            <tr key={a.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
              <Td>
                <div style={{ fontWeight: 700 }}>{a.name}</div>
                <div style={{ fontSize: 11, color: MUTED }}>{a.category}{a.status !== 'active' ? ` · ${a.status}` : ''}</div>
              </Td>
              <Td>{fmtDateDMY(a.purchase_date)}</Td>
              <Td align="right" style={{ fontWeight: 600 }}>{fmtMoney(a.cost_ex_gst)}</Td>
              <Td style={{ fontSize: 11, color: MUTED }}>
                {METHOD_LABELS[a.depreciation_method] || a.depreciation_method}
                {a.useful_life_years ? ` · ${a.useful_life_years}y` : ''}
              </Td>
              <Td align="right" style={{ fontWeight: 700, color: a.fy_deduction > 0 ? GREEN : MUTED }}>
                {fmtMoney(a.fy_deduction)}
              </Td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr style={{ background: '#FAFAF7', borderTop: `2px solid ${BORDER}` }}>
              <Td colSpan={4} style={{ fontWeight: 800, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, color: MUTED }}>
                Total Deductible This FY
              </Td>
              <Td align="right" style={{ fontWeight: 900, fontSize: 14, color: GREEN }}>{fmtMoney(totalDeductible)}</Td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ── 6. Expenses by Category ────────────────────────────────────────────────
function ExpensesByCategory({ rows, totals }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8 }}>
      {rows.length === 0 && (
        <div style={{ padding: 20, color: MUTED, fontSize: 13, textAlign: 'center', gridColumn: '1 / -1' }}>
          No expenses logged this FY.
        </div>
      )}
      {rows.map(c => (
        <div key={c.category} style={{
          padding: '12px 14px', borderRadius: 8,
          background: SUBTLE, border: `1px solid ${BORDER}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: TEXT, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.category}>
              {c.category}
            </span>
            <span style={{ fontSize: 14, fontWeight: 800, color: TEXT, whiteSpace: 'nowrap' }}>
              {fmtMoney(c.total_inc_gst)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: MUTED, marginTop: 2 }}>
            <span>{c.count} item{c.count === 1 ? '' : 's'}</span>
            <span>ex GST {fmtMoney(c.amount_ex_gst)} · GST {fmtMoney(c.gst)}</span>
          </div>
        </div>
      ))}
      {rows.length > 0 && (
        <div style={{
          padding: '12px 14px', borderRadius: 8,
          background: '#FEF3C7', border: '1px solid #FCD34D',
          gridColumn: '1 / -1',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: AMBER, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Grand Total
            </span>
            <span style={{ fontSize: 16, fontWeight: 900, color: AMBER }}>
              {fmtMoney(totals.total_inc_gst)} <span style={{ fontSize: 11, fontWeight: 600 }}>· ex GST {fmtMoney(totals.amount_ex_gst)} · GST {fmtMoney(totals.gst)}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 7. Bank Reconciliation ─────────────────────────────────────────────────
function BankRecon({ rows, onChange }) {
  const [editing, setEditing] = useState(null);

  function startNew() {
    setEditing({ _new: true, account_name: '', book_balance: '', bank_balance: '', last_reconciled: '', notes: '' });
  }

  return (
    <div>
      <div style={{ overflow: 'auto', borderRadius: 8, border: `1px solid ${BORDER}`, marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <Th>Account</Th>
              <Th align="right">Book Balance</Th>
              <Th align="right">Bank Balance</Th>
              <Th align="right">Difference</Th>
              <Th>Last Reconciled</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><Td colSpan={6} align="center" style={{ color: MUTED, padding: 20 }}>No reconciliation snapshots — click "Add account" below.</Td></tr>
            )}
            {rows.map(r => {
              const diff = (Number(r.book_balance) || 0) - (Number(r.bank_balance) || 0);
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <Td><span style={{ fontWeight: 700 }}>{r.account_name}</span></Td>
                  <Td align="right">{fmtMoney(r.book_balance)}</Td>
                  <Td align="right">{fmtMoney(r.bank_balance)}</Td>
                  <Td align="right" style={{ fontWeight: 700, color: Math.abs(diff) < 0.01 ? GREEN : RED }}>
                    {fmtMoney(diff)}
                  </Td>
                  <Td>{r.last_reconciled ? fmtDateDMY(r.last_reconciled) : <span style={{ color: MUTED }}>—</span>}</Td>
                  <Td align="right" style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditing({ ...r })} style={btnGhost}>Edit</button>
                    <button onClick={async () => {
                      if (!window.confirm('Delete this account?')) return;
                      await api.deleteBankReconciliation(r.id);
                      onChange();
                    }} style={{ ...btnGhost, color: RED, marginLeft: 4 }}>Delete</button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button onClick={startNew} style={btnPrimary}>+ Add account</button>

      {editing && (
        <BankReconModal
          row={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </div>
  );
}

function BankReconModal({ row, onCancel, onSaved }) {
  const isNew = !!row._new;
  const [name, setName] = useState(row.account_name || '');
  const [book, setBook] = useState(String(row.book_balance ?? ''));
  const [bank, setBank] = useState(String(row.bank_balance ?? ''));
  const [last, setLast] = useState(row.last_reconciled || '');
  const [notes, setNotes] = useState(row.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setError('');
    if (!name.trim()) { setError('Account name is required.'); return; }
    setSaving(true);
    try {
      const body = {
        account_name: name.trim(),
        book_balance: parseFloat(book) || 0,
        bank_balance: parseFloat(bank) || 0,
        last_reconciled: last || null,
        notes: notes.trim() || null,
      };
      if (isNew) await api.createBankReconciliation(body);
      else        await api.updateBankReconciliation(row.id, body);
      onSaved();
    } catch (e) { setError(e.message || 'Save failed.'); }
    finally    { setSaving(false); }
  }

  return (
    <div style={modalBackdrop}>
      <div style={modalShell}>
        <div style={modalHeader}>
          <div style={{ fontSize: 16, fontWeight: 800, color: TEXT }}>
            {isNew ? 'Add Account' : 'Edit Account'}
          </div>
          <button onClick={onCancel} disabled={saving} style={iconClose}>✕</button>
        </div>
        <div style={modalBody}>
          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}
          <Field label="Account name *">
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. CommBank Business · ACTS TAX" style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Book balance">
              <input type="number" step="0.01" value={book} onChange={e => setBook(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Bank balance">
              <input type="number" step="0.01" value={bank} onChange={e => setBank(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <Field label="Last reconciled">
            <input type="date" value={last} onChange={e => setLast(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Notes">
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Outstanding cheques, transit items, etc."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </Field>
        </div>
        <div style={modalFooter}>
          <button onClick={onCancel} disabled={saving} style={btnGhost}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : isNew ? 'Save' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tiny shared bits ───────────────────────────────────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th style={{
      padding: '10px 12px', fontSize: 11, fontWeight: 700, color: MUTED,
      textAlign: align, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
    }}>{children}</th>
  );
}
function Td({ children, align = 'left', colSpan, style }) {
  return <td colSpan={colSpan} style={{ padding: '10px 12px', textAlign: align, color: TEXT, ...style }}>{children}</td>;
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${BORDER}`, borderRadius: 8, background: '#fff',
  color: TEXT, boxSizing: 'border-box', outline: 'none',
};
const selectStyle = { ...inputStyle, cursor: 'pointer', width: 'auto' };
const btnPrimary = {
  padding: '8px 18px', borderRadius: 8, border: 'none',
  background: NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px', borderRadius: 6, border: `1px solid ${BORDER}`,
  background: '#fff', color: TEXT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalShell = {
  background: '#fff', borderRadius: 14, width: '100%', maxWidth: 500,
  display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const modalHeader = {
  padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const modalBody = { padding: '16px 20px', overflowY: 'auto' };
const modalFooter = {
  padding: '14px 20px', borderTop: `1px solid ${BORDER}`,
  display: 'flex', gap: 10, justifyContent: 'flex-end',
};
const iconClose = {
  background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: 18, padding: 4,
};
